// ---------------------------------------------------------------------------
// Why an answer did not stand.
//
// **Three failures, and a reader is owed the difference between them.** A word
// that was spelled from the board and is simply not in the dictionary at this
// room's band is a near miss. A word that is perfectly good English but uses
// letters that were never on the board is something else entirely: the engine
// refuses an unbuildable word from every human path — `lock-letters`,
// `adopt-draft` and `submit-word` all check — so the only thing that can produce
// one is an AI opponent, whose word is stored unbuildable ON PURPOSE so the room
// can see what the model actually played (DESIGN 6.8, and the four-places note
// in CLAUDE.md). And a host ruling is a person deciding, which is not a claim
// about the word at all.
//
// Calling all three "not a word" is not a wording problem. It is the page making
// a false statement: `BARKS` on `UAIBGQXKR` was reported as a dictionary bug,
// and disproving it cost real time, because BARKS is obviously a word and the
// page said it was not one. Whatever a caller labels these, it may not conflate
// them.
//
// This file is the CLASSIFICATION and nothing else. The labels below are one
// vocabulary for it, and a caller with a tighter row is free to render the same
// kind in fewer words — what must not happen again is two screens working the
// answer out separately and reaching different conclusions about the same word.
// `canBuildFromTiles` is the only check involved and it lives in the shared
// engine, which is also what judging asks.
// ---------------------------------------------------------------------------

import { canBuildFromTiles } from '@countdown/shared';
import type { RoundType } from '@countdown/shared';

export type RuledOutKind =
  /** A person decided this, whichever way. Not a claim about the word. */
  | 'host-ruled'
  /** A real enough word, but not spellable from the nine tiles. */
  | 'not-on-the-board'
  /** Spellable from the tiles, and the dictionary does not have it. */
  | 'not-a-word'
  /** A round with no board to check against: the conundrum and numbers. */
  | 'wrong';

export interface RuledOutInput {
  type: RoundType;
  /** The word as played, whatever anybody ruled about it. */
  word: string;
  /** The board's letters. Empty when the round stored none. */
  tiles: string[];
  /** A host ruling is in force, either way. */
  hostRuled: boolean;
}

/**
 * Which of the three a ruled-out answer is.
 *
 * The host is asked first: a host ruling is in force over whatever the book
 * said, so reporting the dictionary's opinion under it would name a reason that
 * is not the reason.
 *
 * Only a letters round is asked the board question. A conundrum answer has to be
 * an anagram of all nine rather than merely buildable from them, so
 * `canBuildFromTiles` is not the test there and a partial word would come back
 * "on the board" while being nothing of the sort; numbers has no board in this
 * sense at all. Both fall to `wrong`, which is what the reveal already says.
 *
 * **A round with no stored tiles falls to `not-a-word`**, not to
 * `not-on-the-board`. `canBuildFromTiles` against an empty board is false for
 * every word, so the confident answer there would be the accusation this whole
 * file exists to stop, made about every word at once.
 */
export function ruledOutKind({ type, word, tiles, hostRuled }: RuledOutInput): RuledOutKind {
  if (hostRuled) return 'host-ruled';
  if (type !== 'letters') return 'wrong';
  if (tiles.length === 0) return 'not-a-word';
  return canBuildFromTiles(word, tiles) ? 'not-a-word' : 'not-on-the-board';
}

/** Three words at most, because it is drawn as a chip beside the word itself. */
export const RULED_OUT_LABEL: Readonly<Record<RuledOutKind, string>> = {
  'host-ruled': 'host ruled',
  'not-on-the-board': 'not on the board',
  'not-a-word': 'not a word',
  wrong: 'wrong',
};

export function ruledOutLabel(input: RuledOutInput): string {
  return RULED_OUT_LABEL[ruledOutKind(input)];
}
